import {
  Typography,
  FlexBox,
  Checkbox,
  TextInput,
  SPACING,
  Button,
  JoonUIColor,
  Modal,
  ModalHeader,
} from "@joonapp/web-shared"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import Counter from "../../components/counter/Counter"
import { SelectRow } from "../../components/selectRow/SelectRow"
import { SelectRowContainer } from "../../components/selectRowContainer/SelectRowContainer"
import { QUERY_KEYS } from "../../constants"
import { sendBonusReward } from "../../networking/bonusRewards"
import { useFamilyQuery } from "../../networking/queries"
import { useTodaysBonusRewardsQuery } from "../../networking/rewards/queries"
import { FamilyQuerySelect, RewardsTodayType } from "../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { createErrorFromResponse } from "../../util/util"

interface BonusRewardModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void

  selectedChildren: number[]
  selectChild: (id: number) => void
  coinAmount: number
  setCoinAmount: (amount: number) => void
  rewardDescription: string
  setRewardDescription: (description: string) => void
  isSendingReward: boolean
  setIsSendingReward: (isSending: boolean) => void
  error: string | null
  setError: (error: string | null) => void
}

const initialState = {
  isOpen: false,
  selectedChildren: [],
  coinAmount: 10,
  rewardDescription: "",
  isSendingReward: false,
  error: null,
}

export const useBonusRewardModalStore = create<BonusRewardModalStore>(
  (set) => ({
    ...initialState,
    onOpen: () => set({ isOpen: true }),
    onClose: () => set(initialState),
    selectChild: (id: number) => {
      set((state) => ({
        selectedChildren: state.selectedChildren.includes(id)
          ? state.selectedChildren.filter((childId) => childId !== id)
          : [...state.selectedChildren, id],
      }))
    },
    setCoinAmount: (amount) => set({ coinAmount: amount }),
    setRewardDescription: (description) =>
      set({ rewardDescription: description }),
    setIsSendingReward: (isSending) => set({ isSendingReward: isSending }),
    setError: (error) => set({ error }),
  })
)

const BonusRewardModal = () => {
  const {
    isOpen,
    onClose: closeBonusRewardModal,
    selectedChildren,
    selectChild,
    coinAmount,
    setCoinAmount,
    rewardDescription,
    setRewardDescription,
    isSendingReward,
    setIsSendingReward,
    error,
    setError,
  } = useBonusRewardModalStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const childUsers = children?.map((child) => child.user)
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const { data: childrenWhoReceivedRewards } = useTodaysBonusRewardsQuery(
    RewardsTodayType.BONUS
  )

  const childrenWithRewardsAvailable =
    childUsers?.filter(
      (child) => !childrenWhoReceivedRewards?.includes(child.id)
    ) || []

  const sendReward = async () => {
    if (selectedChildren.length === 0) {
      throw setError(t("BRM_ERR_SELECT_CHILD"))
    } else if (rewardDescription.length === 0) {
      throw setError(t("BRM_ERR_DESCRIPTION"))
    }
    setError(null)

    setIsSendingReward(true)

    try {
      const rewardPromises = selectedChildren.map((childId) =>
        sendBonusReward(childId, coinAmount, rewardDescription)
      )
      await Promise.all(rewardPromises)
      trackAnalyticEvent(ANALYTIC_EVENTS.BONUS_REWARD_SENT)
      closeBonusRewardModal()
      queryClient.invalidateQueries([QUERY_KEYS.BONUS_REWARDS])
    } catch (errorResponse) {
      const errorResponseString = createErrorFromResponse(errorResponse)
      if (errorResponseString) setError(errorResponseString)
      else setError(t("REWARD_ERR_GENERIC"))
    }
    setIsSendingReward(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeBonusRewardModal}
      animate
      style={{ width: "min(500px, 100%)" }}
    >
      <ModalHeader
        title={t("REWARD_SEND_BONUS_COIN")}
        onClose={closeBonusRewardModal}
        showBorderBottom
      />
      <div className="flex flex-col p-6 gap-4 pt-4 overflow-y-auto">
        <div className="flex flex-col gap-1">
          <Typography variant="bodyBold">{t("BRM_GIVE_BONUS")}</Typography>
          <Typography variant="bodySmall">{t("BRM_ONE_PER_DAY")}</Typography>
        </div>

        <div className="flex flex-col gap-2 border border-borderPrimary rounded-md p-3">
          <Typography variant="body">{t("AERM_WHO_FOR")}</Typography>
          {childrenWithRewardsAvailable?.length > 0 ? (
            <SelectRowContainer>
              {childrenWithRewardsAvailable?.map((child, i) => (
                <SelectRow
                  key={i}
                  onClick={() => selectChild(child?.id)}
                  hideBorder
                  showBorderBottom={
                    i !== childrenWithRewardsAvailable.length - 1
                  }
                >
                  <Typography variant="bodySmall">{child?.name}</Typography>
                  <Checkbox
                    name="test"
                    onChange={() => {}}
                    selected={selectedChildren.includes(child.id)}
                    hideBorder
                  />
                </SelectRow>
              ))}
            </SelectRowContainer>
          ) : (
            <Typography variant="body">{t("BRM_ALL_SENT")}</Typography>
          )}
        </div>
        <div className="flex flex-col gap-2 border border-borderPrimary rounded-md p-3">
          <Typography variant="body">{t("BRM_SELECT_AMOUNT")}</Typography>
          <Counter value={coinAmount} setValue={setCoinAmount} maxValue={50} />
        </div>
        <FlexBox direction="column" gap={SPACING.space1}>
          <Typography variant="body">{t("BRM_TELL_WHY")}</Typography>
          <TextInput
            name="rewardDescription"
            value={rewardDescription}
            onChange={(e: any) => setRewardDescription(e.target.value)}
            placeholder={t("BRM_PLACEHOLDER")}
            fullWidth
            maxLength={100}
          />
        </FlexBox>
      </div>
      <div className="flex flex-col gap-2 bg-white px-6 py-4 border-t border-borderPrimary">
        {error && (
          <Typography
            variant="bodySmallBold"
            style={{ textAlign: "center", width: "100%" }}
            color={JoonUIColor.semantic.alert}
          >
            {error}
          </Typography>
        )}
        <Button
          text={t("BRM_SEND_REWARD")}
          isLoading={isSendingReward}
          onClick={sendReward}
          fullWidth
        />
      </div>
    </Modal>
  )
}

export default BonusRewardModal
